/**
 * file_ctrl send/receive routing for the web app — offer / accept / decline /
 * progress / pause / resume / cancel / complete / error. Web's mirror of
 * `apps/extension/src/background/fileCtrl.ts`, collapsed into ONE module
 * (extension) since the web app has no separate service-worker context to
 * split "SW-level durable dedup" from "panel-level UI routing" across — this
 * runs entirely on the page.
 *
 * Control messages ride the EXISTING encrypted `/messages` envelope exactly
 * like `notification`/`clipboard` do (tweetnacl via @reflecto/core's
 * encryptMessage/decryptMessage) — `services/notifications.ts`'s
 * `processIncomingMessage` recognizes `parsed.type === "file_ctrl"` and
 * delegates to `dispatchIncomingFileCtrl` here.
 *
 * SECURITY: never log manifest/file content — security.md.
 */

import {
  AEAD_OVERHEAD,
  CHUNK_SIZE,
  CHUNKS_PER_PART,
  decodeBase64,
  encodeBase64,
  encryptMessage,
  type FileCtrl,
  isFileCtrl,
  validateManifest,
} from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import { sendFileCtrlEnvelope } from "../api/client";
import { logWarn } from "../lib/log";
import { addOffer, getOffer, removeOffer } from "./offerStore";
import { terminatePausedOnPeerCtrl } from "./pausedTransferTerminal.web";
import { getReceiverState, makePreAcceptTombstone, saveReceiverState } from "./receiverState";

/** The 4 GiB protocol ceiling, expressed as a part-count bound — mirrors the extension's local derivation (background/fileCtrl.ts) from the same shared @reflecto/core primitives. */
const FT_MAX_PART_COUNT = Math.ceil(
  (4 * 1024 ** 3) / (CHUNKS_PER_PART * (CHUNK_SIZE + AEAD_OVERHEAD))
);

export type FileCtrlHandler = (senderDeviceId: string, ctrl: FileCtrl) => void;

/** Default no-op handler — identity doubles as the "nothing registered" sentinel for the dev double-registration warning below. */
const NOOP_HANDLER: FileCtrlHandler = () => {};

/** Currently registered inbound handler (offer/accept/decline/progress/pause/resume routing to the send/receive UI state). Defaults to a no-op. */
let handler: FileCtrlHandler = NOOP_HANDLER;

/**
 * Subscribe to validated inbound file_ctrl messages. Single-consumer seam —
 * last registration wins (mirrors the extension's one-listener-at-a-time
 * model). Last-wins is acceptable because exactly one consumer exists today:
 * the single MainPage mount's `useFileTransfers`, whose effect cleanup calls
 * `unregisterFileCtrlHandler` — so even React StrictMode's dev double-mount
 * never overlaps two live registrations. The dev-only warning catches the day
 * a second live consumer appears (it would silently steal all ctrl traffic
 * from the first). Production stays silent.
 */
export function registerFileCtrlHandler(onCtrl: FileCtrlHandler): void {
  if (import.meta.env.DEV && handler !== NOOP_HANDLER) {
    // Never logs ctrl/manifest content — registration metadata only (security.md).
    console.warn(
      "registerFileCtrlHandler: replacing a live file_ctrl handler (last-registration-wins). " +
        "Two simultaneous consumers? The previous one stops receiving ctrl messages."
    );
  }
  handler = onCtrl;
}

/** Reset the seam to the default no-op — effect-cleanup counterpart of `registerFileCtrlHandler`. */
export function unregisterFileCtrlHandler(): void {
  handler = NOOP_HANDLER;
}

/**
 * Encrypt `{ type: "file_ctrl", payload: ctrl }` for `recipientDeviceId` and
 * POST it via `sendFileCtrlEnvelope` (`POST /file-ctrl`). Throws `"not_paired"`
 * if the app has no stored keys/token yet.
 */
export async function sendFileCtrl(recipientDeviceId: string, ctrl: FileCtrl): Promise<void> {
  const { token, secretKey, phonePubKey } = await webStorage.get(
    "token",
    "secretKey",
    "phonePubKey"
  );
  if (!token || !secretKey || !phonePubKey) {
    throw new Error("not_paired");
  }
  const envelope = { type: "file_ctrl" as const, payload: ctrl };
  const plaintext = new TextEncoder().encode(JSON.stringify(envelope));
  const wire = encryptMessage(plaintext, decodeBase64(phonePubKey), decodeBase64(secretKey));
  await sendFileCtrlEnvelope(token, encodeBase64(wire), crypto.randomUUID(), recipientDeviceId);
}

/**
 * Validate and route a decrypted `file_ctrl` envelope — called from
 * `services/notifications.ts`'s decrypted-envelope router once it recognizes
 * the wire `type: "file_ctrl"` tag. Never throws — every failure path is a
 * silent drop, per the same rule `isFileCtrl` documents for unknown `kind`
 * values.
 */
/**
 * Exhaustiveness pin for `dispatchIncomingFileCtrl`'s page-side routing. EVERY
 * kind is relayed onward to the registered `handler`; this table records which
 * ones ALSO get pre-processing before that relay (mirrors the extension SW's
 * `_swCtrlRouting`). Never read at runtime — the `satisfies` is the point: a
 * 13th `FileCtrl["kind"]` becomes a COMPILE ERROR here, forcing an explicit
 * routing decision instead of the silent default a kind-keyed `if` chain gives.
 */
const _pageCtrlRouting = {
  offer: "preprocess", // validate manifest + sender, dedup, auto-decline oversized
  cancel: "preprocess", // pending-offer tombstone + PAUSED-record terminal transition
  error: "preprocess", // PAUSED-record terminal transition
  accept: "relay-only",
  decline: "relay-only",
  offer_received: "relay-only",
  progress: "relay-only",
  pause: "relay-only",
  resume: "relay-only",
  complete: "relay-only",
  p2p_signal: "relay-only",
  demote: "relay-only",
} satisfies Record<FileCtrl["kind"], "preprocess" | "relay-only">;

export async function dispatchIncomingFileCtrl(
  senderDeviceId: string,
  parsedUnknown: unknown
): Promise<void> {
  if (!isFileCtrl(parsedUnknown)) {
    logWarn("filetransfer", "file_ctrl_malformed");
    return;
  }
  const { payload: ctrl } = parsedUnknown;

  if (ctrl.kind === "offer") {
    if (validateManifest(ctrl.manifest) !== null) {
      logWarn("filetransfer", "file_ctrl_offer_invalid");
      return;
    }
    if (ctrl.manifest.senderDeviceId !== senderDeviceId) {
      logWarn("filetransfer", "file_ctrl_offer_sender_mismatch");
      return;
    }
    const transferId = ctrl.manifest.transferId;

    // §8.3 durable dedup: check the persisted receiver store BEFORE notifying.
    // ANY record (active or terminal tombstone) → drop without notifying. A
    // replayed offer on an ACTIVE "transferring" record re-sends accept
    // (heals a lost accept, §8.4); "paused" and every tombstone status drop
    // with no re-accept (paused never auto-resumes).
    const existing = await getReceiverState(transferId);
    if (existing) {
      if (existing.status === "transferring") {
        const acceptCtrl: FileCtrl =
          existing.chunksVerified > 0
            ? { kind: "accept", transferId, resumeFromChunk: existing.chunksVerified }
            : { kind: "accept", transferId };
        try {
          await sendFileCtrl(senderDeviceId, acceptCtrl);
        } catch {
          logWarn("filetransfer", "file_ctrl_reaccept_failed");
        }
      }
      return; // drop without notifying (no addOffer, no handler broadcast)
    }

    // Oversized-offer auto-decline (mirrors Android's silent-decline path,
    // reason "unsupported") — write the tombstone BEFORE the decline ctrl so a
    // replayed oversized offer is durably dropped by the dedup above.
    const partCount = Math.ceil(ctrl.manifest.files[0].chunkCount / CHUNKS_PER_PART);
    if (partCount > FT_MAX_PART_COUNT) {
      await saveReceiverState(makePreAcceptTombstone(transferId, senderDeviceId, "declined"));
      try {
        await sendFileCtrl(senderDeviceId, { kind: "decline", transferId, reason: "unsupported" });
      } catch {
        logWarn("filetransfer", "file_ctrl_auto_decline_failed");
      }
      return;
    }
    addOffer(ctrl.manifest); // dedupes by transferId internally
  }

  if (ctrl.kind === "cancel") {
    // Pending-offer cancel (pre-accept, in-memory only): remove + tombstone
    // so a later offer replay can't re-register/re-notify.
    const pending = getOffer(ctrl.transferId);
    if (pending) {
      removeOffer(ctrl.transferId);
      await saveReceiverState(makePreAcceptTombstone(ctrl.transferId, senderDeviceId, "cancelled"));
    }
  }

  if (ctrl.kind === "cancel" || ctrl.kind === "error") {
    // A PAUSED record has no live loop — terminal-transition it directly
    // (mirrors the extension's background/pausedTransferTerminal.ts; on web
    // there's no separate idle-closed host context, so this always runs in
    // the SAME page that would otherwise host a live loop). No-op for any
    // other status (an ACTIVE record's abort is handled by receiveTransfer.web.ts's
    // own live-loop gate, reached via the `handler` callback below).
    await terminatePausedOnPeerCtrl(ctrl);
  }

  handler(senderDeviceId, ctrl);
}
